import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator,
} from '../../components/ui/input-otp';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  Phone, Coins, KeyRound, Loader2, ShieldCheck, CheckCircle2,
  AlertTriangle, RefreshCw, Send, ArrowLeft, MessageSquare, MessageCircle,
  Hourglass, Sparkles, User, Clock, Wallet
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Admin → Points → Manual OTP Redeem
//
// Phase 3 of the Scratch Points Wallet System.
//
// Flow:
//   STEP 1 (entry)   → admin types phone + amount → "Send OTP"
//                       calls /admin/wallet/initiate-otp-redeem
//   STEP 2 (otp)     → backend returns session_id (amount LOCKED). Admin
//                       collects the 6-digit OTP from the customer and types
//                       it. → "Confirm Redemption"
//                       calls /admin/wallet/confirm-otp-redeem
//   STEP 3 (done)    → success screen with txn id + new balance.
//
// Safety / UX rules enforced in this UI:
//   • Amount field is DISABLED + READ-ONLY once the OTP step starts.
//   • A live 5:00 countdown ticks down; at 0 the UI auto-locks and prompts
//     to re-initiate.
//   • Wrong OTP shows attempts_remaining; at 0 attempts the session is
//     marked FAILED and we send the admin back to step 1.
//   • Re-initiate / "Start Over" wipes ALL local state.
// ─────────────────────────────────────────────────────────────────────────────

const STEP_ENTRY  = 'entry';
const STEP_OTP    = 'otp';
const STEP_DONE   = 'done';

const VALID_PHONE_REGEX = /^[6-9]\d{9}$/;

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function PointsManualRedeemPage() {
  const [step, setStep] = useState(STEP_ENTRY);

  // Step 1 inputs
  const [phone, setPhone]   = useState('');
  const [amount, setAmount] = useState('');
  const [initiating, setInitiating] = useState(false);

  // Step 2 / session state
  const [session, setSession] = useState(null);   // initiate response
  const [otp, setOtp]         = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const tickerRef = useRef(null);

  // Step 3 confirmation
  const [confirmation, setConfirmation] = useState(null);

  // ── Countdown ticker ─────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== STEP_OTP || !session) return undefined;
    tickerRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(tickerRef.current);
  }, [step, session]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    setStep(STEP_ENTRY);
    setPhone('');
    setAmount('');
    setSession(null);
    setOtp('');
    setSecondsLeft(0);
    setConfirming(false);
    setConfirmation(null);
    setAttemptsRemaining(3);
    if (tickerRef.current) clearInterval(tickerRef.current);
  }, []);

  const phoneValid = VALID_PHONE_REGEX.test(phone.trim());
  const amountNum  = parseInt(amount, 10);
  const amountValid = Number.isInteger(amountNum) && amountNum > 0;
  const expired = step === STEP_OTP && secondsLeft <= 0;

  // ── Step 1 → initiate ────────────────────────────────────────────────────
  const handleInitiate = async (e) => {
    e?.preventDefault();
    if (!phoneValid)  { toast.error('Enter a valid 10-digit Indian mobile number'); return; }
    if (!amountValid) { toast.error('Enter a valid amount greater than 0');         return; }

    setInitiating(true);
    try {
      const res  = await adminApi.initiateOTPRedeem(phone.trim(), amountNum);
      const data = res.data;
      setSession(data);
      setAttemptsRemaining(data.attempts_remaining ?? data.max_attempts ?? 3);
      setSecondsLeft(Math.max(0, data.ttl_seconds || 300));
      setOtp('');
      setStep(STEP_OTP);
      toast.success(
        `OTP sent via ${data.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} to ${data.customer?.full_name || data.customer?.phone}`
      );
    } catch (err) {
      const d   = err?.response?.data?.detail;
      const msg = (typeof d === 'object' && d?.message) ? d.message : (d || 'Failed to send OTP');
      toast.error(msg);
    } finally {
      setInitiating(false);
    }
  };

  // ── Step 2 → confirm ─────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!session?.session_id) return;
    if (!/^\d{6}$/.test(otp)) {
      toast.error('Enter the 6-digit OTP from the customer');
      return;
    }
    if (expired) {
      toast.error('Session expired. Please re-initiate.');
      return;
    }
    setConfirming(true);
    try {
      const res  = await adminApi.confirmOTPRedeem(session.session_id, otp);
      setConfirmation(res.data);
      setStep(STEP_DONE);
      toast.success(`Redeemed ${res.data.redeemed} pts from ${res.data.customer?.full_name || 'customer'}`);
    } catch (err) {
      const d    = err?.response?.data?.detail;
      const code = (typeof d === 'object' ? d?.code : null);
      const msg  = (typeof d === 'object' && d?.message) ? d.message : (d || 'Confirmation failed');
      if (typeof d === 'object' && typeof d.attempts_remaining === 'number') {
        setAttemptsRemaining(d.attempts_remaining);
      }
      toast.error(msg);
      // Terminal session states → bounce back to step 1 cleanly
      if (['EXPIRED', 'TOO_MANY_ATTEMPTS', 'ALREADY_USED'].includes(code)) {
        setTimeout(() => { reset(); }, 1500);
      } else {
        // Wrong OTP — clear it so admin can retype
        setOtp('');
      }
    } finally {
      setConfirming(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <AdminShell title="Points · Manual OTP">
      <div
        className="max-w-2xl mx-auto space-y-6"
        data-testid="admin-points-manual-redeem-page"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#F2F2F2] flex items-center gap-2">
              <KeyRound size={20} className="text-[#D4A843]" /> Manual OTP Redemption
            </h1>
            <p className="text-sm text-[#B9B9B9] mt-1">
              Use this fallback when the customer cannot show their wallet QR.
              An OTP is sent to their phone and is valid for 5 minutes.
            </p>
          </div>

          {step !== STEP_ENTRY && (
            <Button
              variant="outline"
              onClick={reset}
              className="border-[rgba(255,255,255,0.1)] text-[#B9B9B9] hidden sm:inline-flex"
              data-testid="manual-redeem-reset-btn"
            >
              <ArrowLeft size={14} className="mr-1" /> Start Over
            </Button>
          )}
        </div>

        {/* Stepper */}
        <Stepper step={step} />

        {/* ─────────── STEP 1 — ENTRY ─────────── */}
        {step === STEP_ENTRY && (
          <Card
            data-testid="manual-redeem-step-entry"
            className="p-6 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] space-y-5"
          >
            <form onSubmit={handleInitiate} className="space-y-5">
              {/* Phone */}
              <div className="space-y-2">
                <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                  <Phone size={11} /> Customer phone (10-digit Indian mobile)
                </Label>
                <Input
                  data-testid="manual-redeem-phone-input"
                  inputMode="numeric"
                  placeholder="9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] text-base h-12"
                  autoComplete="off"
                  autoFocus
                />
                {phone && !phoneValid && (
                  <p className="text-[11px] text-[#FF8A8A]">
                    Must be a 10-digit number starting with 6/7/8/9.
                  </p>
                )}
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                  <Coins size={11} /> Points to redeem
                </Label>
                <Input
                  data-testid="manual-redeem-amount-input"
                  type="number"
                  min={1}
                  placeholder="e.g. 100"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] text-2xl font-mono h-14 text-center"
                />
                <div className="flex gap-2 justify-center">
                  {[100, 250, 500, 1000].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setAmount(String(preset))}
                      className="px-3 py-1 rounded-lg bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] text-[11px] text-[#B9B9B9] transition-colors"
                      data-testid={`manual-redeem-preset-${preset}`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="manual-redeem-send-otp-btn"
                disabled={!phoneValid || !amountValid || initiating}
                className="w-full h-12 btn-gold rounded-xl font-semibold text-base"
              >
                {initiating
                  ? (<><Loader2 size={16} className="animate-spin mr-2" /> Sending OTP…</>)
                  : (<><Send size={15} className="mr-2" /> Send OTP & Continue</>)}
              </Button>

              <div className="flex items-start gap-2 text-[11px] text-[#6B6B6B] bg-[rgba(212,168,67,0.05)] border border-[rgba(212,168,67,0.18)] rounded-xl px-3 py-2">
                <ShieldCheck size={12} className="text-[#D4A843] flex-shrink-0 mt-0.5" />
                <span>
                  The amount you enter here is locked at OTP generation. It cannot be changed during confirmation — the customer's OTP only authorises THIS specific amount.
                </span>
              </div>
            </form>
          </Card>
        )}

        {/* ─────────── STEP 2 — OTP ─────────── */}
        {step === STEP_OTP && session && (
          <Card
            data-testid="manual-redeem-step-otp"
            className="p-6 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] space-y-5"
          >
            {/* Customer + amount summary */}
            <div className="flex items-start justify-between gap-3 pb-4 border-b border-[rgba(255,255,255,0.06)]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(212,168,67,0.12)]">
                  <User size={18} className="text-[#D4A843]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#F2F2F2] truncate" data-testid="manual-redeem-customer-name">
                    {session.customer?.full_name || '—'}
                  </p>
                  <p className="text-xs text-[#B9B9B9] flex items-center gap-1 mt-0.5">
                    <Phone size={10} /> {session.customer?.phone || session.customer?.phone_e164}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">Amount</p>
                <p
                  className="text-2xl font-mono font-bold text-[#D4A843]"
                  data-testid="manual-redeem-locked-amount"
                >
                  {session.amount}
                </p>
                <p className="text-[10px] text-[#6B6B6B]">pts · locked</p>
              </div>
            </div>

            {/* Balance breakdown */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-xl bg-[rgba(212,168,67,0.06)] border border-[rgba(212,168,67,0.2)]">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Coins size={11} className="text-[#D4A843]" />
                  <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">Available</p>
                </div>
                <p
                  className="text-2xl font-bold font-mono text-[#D4A843]"
                  data-testid="manual-redeem-balance-available"
                >
                  {session.balance?.available ?? 0}
                </p>
              </div>
              <div className="text-center p-3 rounded-xl bg-[rgba(255,77,77,0.04)] border border-[rgba(255,77,77,0.18)]">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Hourglass size={11} className="text-[#FF8A8A]" />
                  <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">Expiring</p>
                </div>
                <p className="text-2xl font-bold font-mono text-[#FF8A8A]">
                  {session.balance?.expiring ?? 0}
                </p>
              </div>
              <div className="text-center p-3 rounded-xl bg-[rgba(96,165,250,0.04)] border border-[rgba(96,165,250,0.18)]">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Sparkles size={11} className="text-[#60A5FA]" />
                  <p className="text-[10px] text-[#B9B9B9] uppercase tracking-widest">Loyalty</p>
                </div>
                <p className="text-2xl font-bold font-mono text-[#60A5FA]">
                  {session.balance?.non_expiring ?? 0}
                </p>
              </div>
            </div>

            {/* Channel + countdown */}
            <div className="flex items-center justify-between gap-3 px-1">
              <div className="flex items-center gap-2 text-xs text-[#B9B9B9]">
                {session.channel === 'whatsapp' ? (
                  <MessageCircle size={14} className="text-[#4ADE80]" />
                ) : (
                  <MessageSquare size={14} className="text-[#60A5FA]" />
                )}
                OTP sent via{' '}
                <span className="font-semibold text-[#F2F2F2] uppercase">
                  {session.channel || 'SMS'}
                </span>
              </div>
              <div
                className={
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono ' +
                  (expired
                    ? 'bg-[rgba(255,77,77,0.1)] text-[#FF8A8A] border border-[rgba(255,77,77,0.3)]'
                    : secondsLeft <= 30
                      ? 'bg-[rgba(251,185,36,0.1)] text-[#FBB924] border border-[rgba(251,185,36,0.3)]'
                      : 'bg-[rgba(74,222,128,0.06)] text-[#4ADE80] border border-[rgba(74,222,128,0.25)]')
                }
                data-testid="manual-redeem-countdown"
              >
                <Clock size={11} />
                {expired ? 'EXPIRED' : formatTime(secondsLeft)}
              </div>
            </div>

            {/* OTP input */}
            <div className="space-y-3">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                <KeyRound size={11} /> 6-digit OTP from customer
              </Label>
              <div className="flex justify-center" data-testid="manual-redeem-otp-wrapper">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  disabled={confirming || expired}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} data-testid="otp-slot-0" />
                    <InputOTPSlot index={1} data-testid="otp-slot-1" />
                    <InputOTPSlot index={2} data-testid="otp-slot-2" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} data-testid="otp-slot-3" />
                    <InputOTPSlot index={4} data-testid="otp-slot-4" />
                    <InputOTPSlot index={5} data-testid="otp-slot-5" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Badge
                  variant="outline"
                  className={
                    attemptsRemaining === 0
                      ? 'border-[rgba(255,77,77,0.4)] text-[#FF8A8A]'
                      : attemptsRemaining <= 1
                        ? 'border-[rgba(251,185,36,0.4)] text-[#FBB924]'
                        : 'border-[rgba(255,255,255,0.1)] text-[#B9B9B9]'
                  }
                  data-testid="manual-redeem-attempts-remaining"
                >
                  {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} remaining
                </Badge>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={reset}
                className="border-[rgba(255,255,255,0.1)] text-[#B9B9B9]"
                data-testid="manual-redeem-restart-btn"
              >
                <RefreshCw size={14} className="mr-2" /> Restart
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={confirming || expired || otp.length !== 6}
                className="flex-1 h-12 btn-gold rounded-xl font-semibold"
                data-testid="manual-redeem-confirm-btn"
              >
                {confirming
                  ? (<><Loader2 size={16} className="animate-spin mr-2" /> Verifying…</>)
                  : (<>Confirm — Redeem {session.amount} pts</>)}
              </Button>
            </div>

            {expired && (
              <div className="p-3 rounded-xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.3)] flex items-start gap-3">
                <AlertTriangle size={15} className="text-[#FF4D4D] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#FF8A8A]">
                  Session expired. Click <strong>Restart</strong> and send a new OTP.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* ─────────── STEP 3 — DONE ─────────── */}
        {step === STEP_DONE && confirmation && (
          <Card
            data-testid="manual-redeem-step-done"
            className="p-6 rounded-2xl border"
            style={{
              background: 'rgba(74,222,128,0.06)',
              borderColor: 'rgba(74,222,128,0.35)',
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(74,222,128,0.18)' }}
              >
                <CheckCircle2 size={22} className="text-[#4ADE80]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-[#F2F2F2]">Redemption Successful</h2>
                <p className="text-[#B9B9B9] text-sm mt-1">
                  <strong className="text-[#4ADE80] font-mono">
                    {confirmation.redeemed} pts
                  </strong>{' '}
                  deducted from{' '}
                  <strong className="text-[#F2F2F2]">
                    {confirmation.customer?.full_name}
                  </strong>
                </p>
                <div className="text-[11px] text-[#6B6B6B] mt-3 space-y-0.5">
                  <p>
                    New balance:{' '}
                    <span className="text-[#D4A843] font-mono font-semibold">
                      {confirmation.balance?.available}
                    </span>{' '}
                    pts
                  </p>
                  <p>
                    Txn ID:{' '}
                    <code className="text-[#6B6B6B]">
                      {confirmation.transaction?.id?.slice(0, 8)}…
                    </code>
                    {' · '}
                    Session:{' '}
                    <code className="text-[#6B6B6B]">
                      {confirmation.session_id?.slice(0, 8)}…
                    </code>
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button
                onClick={reset}
                className="flex-1 btn-gold rounded-xl"
                data-testid="manual-redeem-next-btn"
              >
                <KeyRound size={14} className="mr-2" /> Redeem for Next Customer
              </Button>
            </div>
          </Card>
        )}
      </div>
    </AdminShell>
  );
}


// ─── Small helper: 3-step progress indicator ──────────────────────────────
function Stepper({ step }) {
  const items = [
    { id: STEP_ENTRY, label: 'Enter Details', icon: Wallet },
    { id: STEP_OTP,   label: 'Verify OTP',    icon: KeyRound },
    { id: STEP_DONE,  label: 'Confirmation',  icon: CheckCircle2 },
  ];
  const idx = items.findIndex((i) => i.id === step);
  return (
    <div className="flex items-center justify-between px-1" data-testid="manual-redeem-stepper">
      {items.map((it, i) => {
        const active = i === idx;
        const done   = i < idx;
        const Icon   = it.icon;
        return (
          <React.Fragment key={it.id}>
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div
                className={
                  'w-9 h-9 rounded-full border flex items-center justify-center transition-colors ' +
                  (done
                    ? 'bg-[rgba(74,222,128,0.12)] border-[rgba(74,222,128,0.5)] text-[#4ADE80]'
                    : active
                      ? 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.55)] text-[#D4A843]'
                      : 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)] text-[#6B6B6B]')
                }
              >
                <Icon size={15} />
              </div>
              <p
                className={
                  'text-[10px] uppercase tracking-widest ' +
                  (done ? 'text-[#4ADE80]' : active ? 'text-[#D4A843]' : 'text-[#6B6B6B]')
                }
              >
                {it.label}
              </p>
            </div>
            {i < items.length - 1 && (
              <div
                className={
                  'flex-1 h-px mx-2 ' +
                  (i < idx ? 'bg-[rgba(74,222,128,0.4)]' : 'bg-[rgba(255,255,255,0.06)]')
                }
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
